import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import dayjs from "dayjs";
import { formatMoney } from "../../../utils/currency";

const PAPER_FONT = "Arial, Helvetica, sans-serif";
const BORDER = "1px solid #000";
const COLUMNS = "1fr 110px 130px 110px 130px";

// Como el PDF emitido: importes sin símbolo y con 2 decimales.
const plain = (value, currency) => formatMoney(value, currency, { symbol: false });

const discountText = (line, currency) => {
  if (line.discountType === "percent" && line.discountValue > 0) return `${line.discountValue}%`;
  if (line.discountType === "amount" && line.discountValue > 0) return plain(line.discountValue, currency);
  return "-";
};

const Cell = ({ children, align = "left", header = false, last = false }) => (
  <Box
    sx={{
      px: 0.75,
      py: 0.4,
      textAlign: align,
      fontWeight: header ? 700 : 400,
      bgcolor: header ? "#f0f0f0" : "transparent",
      borderRight: last ? "none" : BORDER,
      wordBreak: "break-word",
      fontSize: 14,
    }}
  >
    {children}
  </Box>
);

/**
 * Vista previa orientativa de la factura antes de emitirla, armada para parecerse al PDF emitido. No es el CFE:
 * serie, número, CAE y QR los asigna el proveedor al emitir, y el formato final puede diferir.
 * `lines` llega normalizado: { description, quantity, price, discountType, discountValue, total } con números.
 * `issuerName`/`issuerAddress`/`issuerRut` son los datos fiscales del emisor (los del RUT de la sucursal elegida).
 */
const InvoicePreviewModal = ({
  open,
  onClose,
  documentLabel,
  isCreditNote,
  logoUrl,
  issuerName,
  issuerAddress,
  issuerRut,
  date,
  consumerFinal,
  rut,
  razonSocial,
  adenda,
  reference,
  lines,
  currency,
  subtotal,
  iva,
  total,
}) => {
  const isForeign = currency !== "UYU";
  return (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>Vista previa</DialogTitle>
    <DialogContent dividers sx={{ bgcolor: "action.hover" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        Orientativa: el comprobante real se genera al emitir y puede diferir en el formato.
      </Typography>
      {adenda && !isCreditNote && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Adenda: {adenda}
        </Typography>
      )}

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: "#fff",
          color: "#000",
          fontFamily: PAPER_FONT,
          borderRadius: 1,
          boxShadow: 2,
          p: { xs: 2, md: 5 },
        }}
      >
        <Typography
          aria-hidden
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-30deg)",
            fontSize: 96,
            fontWeight: 700,
            letterSpacing: 8,
            color: "rgba(0,0,0,0.06)",
            pointerEvents: "none",
            userSelect: "none",
            fontFamily: PAPER_FONT,
          }}
        >
          BORRADOR
        </Typography>

        {/* Emisor (izquierda) y tipo de comprobante (derecha) */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 3 }}>
          <Box sx={{ fontSize: 15, lineHeight: 1.7 }}>
            {logoUrl && <Box component="img" src={logoUrl} alt="Logo" sx={{ display: "block", maxHeight: 56, maxWidth: 180, objectFit: "contain", mb: 1 }} />}
            <Box sx={{ fontWeight: 700 }}>{issuerName || "-"}</Box>
            {issuerAddress && <Box>{issuerAddress}</Box>}
            <Box>RUT: {issuerRut || "-"}</Box>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Box sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2 }}>{documentLabel}</Box>
            <Box sx={{ fontSize: 14, fontWeight: 700 }}>Serie y N.º: se asignan al emitir</Box>
            <Box sx={{ fontSize: 15 }}>Fecha: {date ? dayjs(date).format("DD/MM/YYYY") : "-"}</Box>
            <Box sx={{ fontSize: 15 }}>Moneda: {currency}</Box>
            {reference && <Box sx={{ fontSize: 13 }}>Referencia: {reference}</Box>}
          </Box>
        </Box>

        <Box sx={{ borderTop: "1px solid #ddd", pt: 1.5, mb: 3, fontSize: 15, lineHeight: 1.7 }}>
          {consumerFinal ? (
            <Box sx={{ fontWeight: 700 }}>Cliente: Consumidor final</Box>
          ) : (
            <>
              <Box sx={{ fontWeight: 700 }}>Cliente: {razonSocial || "-"}</Box>
              <Box>RUT: {rut || "-"}</Box>
            </>
          )}
        </Box>

        {/* Detalle */}
        <Box sx={{ border: BORDER }}>
          <Box sx={{ display: "grid", gridTemplateColumns: COLUMNS, borderBottom: BORDER }}>
            <Cell header>Descripción</Cell>
            <Cell header align="center">Cantidad</Cell>
            <Cell header align="center">Precio unit.</Cell>
            <Cell header align="center">Dto.</Cell>
            <Cell header align="center" last>Importe</Cell>
          </Box>
          {lines.map((line, index) => (
            <Box key={index} sx={{ display: "grid", gridTemplateColumns: COLUMNS, borderBottom: index < lines.length - 1 ? BORDER : "none" }}>
              <Cell>{line.description}</Cell>
              <Cell align="right">{plain(line.quantity, currency)}</Cell>
              <Cell align="right">{plain(line.price, currency)}</Cell>
              <Cell align="center">{discountText(line, currency)}</Cell>
              <Cell align="right" last>{plain(line.total, currency)}</Cell>
            </Box>
          ))}
        </Box>

        {/* Totales */}
        <Box sx={{ ml: "auto", mt: 3, maxWidth: 320, fontSize: 15, lineHeight: 1.8 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>Subtotal:</span>
            <span>{plain(subtotal, currency)}</span>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <span>IVA:</span>
            <span>{plain(iva, currency)}</span>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 20 }}>
            <span>{isForeign ? `Total (${currency}):` : "Total:"}</span>
            <span>{plain(total, currency)}</span>
          </Box>
        </Box>

        {/* Pie fiscal: lo asigna el proveedor al emitir */}
        <Box sx={{ borderTop: "1px solid #ddd", mt: 3, pt: 2.5, display: "flex", gap: 4, alignItems: "center" }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              border: "1px dashed #999",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            QR
          </Box>
          <Box sx={{ fontSize: 13, lineHeight: 1.9 }}>
            <Box>Cód. de Seguridad: se asigna al emitir</Box>
            <Box>Nro CAE, rango y vencimiento: se asignan al emitir</Box>
            <Box>Puede verificar este comprobante en:</Box>
            <Box>https://www.dgi.gub.uy</Box>
          </Box>
        </Box>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cerrar</Button>
    </DialogActions>
  </Dialog>
  );
};

export default InvoicePreviewModal;
